/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  FunctionComponent,
  useEffect,
  useContext,
  useMemo,
  useState,
  MutableRefObject,
  useRef,
} from 'react';
import {
  useSVFViewer,
  ViewableInfo,
} from '~src/hooks/svf-viewer-hooks/useSVFViewer';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbFile, DbModuleFile, DbTenderSVFAttachment } from '~src/types/db';
import { useIntl } from 'react-intl';
import { appLog } from '~src/utils/log';
import { Button, Spin } from 'antd';
import fileVersionsContext from '~src/contexts/FileVersionsContext';
import ViewSelector from './ViewSelector';

type SVFViewerProps = {
  fileToView?: DbFile | DbModuleFile | DbTenderSVFAttachment | undefined;
  onClose?: () => void;
  showTitle?: boolean;
  titleText?: string;
  viewerRef?: MutableRefObject<Autodesk.Viewing.GuiViewer3D | null>;
  onViewablesChange?: (viewables: ViewableInfo[]) => void;
  externalGuid?: string;
};

export const SVFViewer: FunctionComponent<SVFViewerProps> = ({
  fileToView,
  onClose,
  showTitle = true,
  titleText,
  viewerRef,
  onViewablesChange,
  externalGuid,
}) => {
  const intl = useIntl();

  const {
    status,
    initViewer,
    loadModels,
    getUrn,
    viewer3D,
    viewables,
    loadViewableByGuid,
  } = useSVFViewer(fileToView);
  const styles = useStyles(styleFn);

  const { fileVersionsContext: versionsCtx } = useContext(
    fileVersionsContext as any
  );
  const resolvedTitle = useMemo(() => {
    if (titleText) return titleText;

    const directTitle = (fileToView as any)?.title;
    if (directTitle) return directTitle;

    const version = fileToView ? versionsCtx?.[fileToView.id] : null;
    return version ? `${version.name} (version ${version.revision})` : '';
  }, [titleText, fileToView, versionsCtx]);

  // keep current guid valid
  const [activeGuid, setActiveGuid] = useState<string | undefined>(undefined);
  useEffect(() => setActiveGuid(undefined), [fileToView?.id]);
  const safeActiveGuid = useMemo(() => {
    if (!Array.isArray(viewables) || viewables.length === 0) return undefined;
    const exists = activeGuid && viewables.some((v) => v.guid === activeGuid);
    return exists ? activeGuid : viewables[0]?.guid;
  }, [viewables, activeGuid]);

  useEffect(() => {
    if (!Array.isArray(viewables) || viewables.length === 0) return;
    const exists = activeGuid && viewables.some((v) => v.guid === activeGuid);
    if (!exists) {
      // console.log('[SVFViewer] healing active guid ->', viewables[0]?.guid);
      setActiveGuid(viewables[0]?.guid);
    }
  }, [viewables]);

  // serialize opens
  const openTokenRef = useRef(0);

  useEffect(() => {
    let viewerInstance: Autodesk.Viewing.GuiViewer3D | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    const myToken = ++openTokenRef.current;

    const initAndLoad = async (): Promise<boolean> => {
      const fileName =
        (fileToView as any)?.title ||
        (fileToView as any)?.file_name ||
        'undefined';
      const extension = fileName?.split('.').pop()?.toLowerCase() || 'unknown';

      appLog(
        `[SVFViewer Component] Opening viewer for file: ${fileName}, extension: ${extension}`
      );

      const viewerDiv = document.getElementById('viewer-3d');
      if (viewerDiv) {
        appLog(
          `[SVFViewer Component] Clearing viewer div before opening for file: ${fileName}`
        );
        viewerDiv.innerHTML = '';
      }

      await new Promise((resolve) => {
        timeoutId = setTimeout(resolve, 100);
      });

      if (myToken !== openTokenRef.current) return false;

      const viewerData = await getUrn();
      if (viewerData == null) {
        appLog(`[SVFViewer Component] Failed to get URN for file`);
        return false;
      }
      if (myToken !== openTokenRef.current) return false;

      appLog(`[SVFViewer Component] Got URN, initializing viewer`);
      const viewer = await initViewer(viewerData.access_token, onClose);
      if (!viewer || myToken !== openTokenRef.current) return false;

      viewerInstance = viewer;
      if (viewerRef) viewerRef.current = viewer;

      const onGeom = () => {
        try {
          viewer.resize();
        } catch {}
        viewer.removeEventListener(
          Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
          onGeom
        );
      };
      viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onGeom);

      const res = await loadModels(viewer, viewerData.urn);
      return res.ok ? true : false;
    };

    const open = async () => {
      const ok1 = await initAndLoad();
      if (ok1 || myToken !== openTokenRef.current) return;

      // Escalate once on failure: full shutdown + clean host + retry
      appLog(
        `[SVFViewer Component] First load failed, escalating reset + retry (if error 4)`
      );

      try {
        // @ts-ignore
        Autodesk.Viewing.shutdown && Autodesk.Viewing.shutdown();
      } catch {}

      const host = document.getElementById('viewer-3d');
      if (host) host.innerHTML = '';

      await new Promise((r) => setTimeout(r, 200));
      await initAndLoad();
    };

    open();

    // Cleanup
    return () => {
      appLog(`[SVFViewer Component] Cleanup started`);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (viewerInstance) {
        try {
          viewerInstance.finish();
        } catch (error) {
          appLog(`[SVFViewer Component] Error finishing viewer:`, error);
        }
        viewerInstance = null;
      }

      const host = document.getElementById('viewer-3d');
      if (host) host.innerHTML = '';

      appLog(`[SVFViewer Component] Cleanup completed`);
    };
  }, [fileToView?.id]);

  // when viewables are updated, we throw them to the top (modal)
  useEffect(() => {
    if (onViewablesChange) onViewablesChange(viewables || []);
  }, [viewables, onViewablesChange]);

  // By default, we take the first viewable
  useEffect(() => {
    if (externalGuid && externalGuid !== safeActiveGuid) {
      // console.log('[SVFViewer] externalGuid ->', externalGuid);
      setActiveGuid(externalGuid);
      if (viewer3D) loadViewableByGuid(viewer3D, externalGuid);
    }
  }, [externalGuid, safeActiveGuid, viewer3D, loadViewableByGuid]);

  if (status.loading) {
    return (
      <div css={styles.messageContainer}>
        <Spin />
        <p css={styles.infoMessage}>{status.message}</p>
      </div>
    );
  }
  if (status.error) {
    return (
      <div css={styles.messageContainer}>
        <p css={styles.errorMessage}>
          {intl.formatMessage({ id: 'opened-svf-viewer-error' })}
        </p>
        <Button onClick={onClose}>{intl.formatMessage({ id: 'close' })}</Button>
      </div>
    );
  }

  return (
    <div css={styles.viewerWrapper}>
      {showTitle && (
        <div css={styles.titleBar}>
          <div css={styles.titleText} title={resolvedTitle}>
            {resolvedTitle}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ViewSelector
              viewables={viewables}
              activeGuid={safeActiveGuid}
              onChange={(guid) => {
                // console.log('Switch to viewable guid:', guid);
                setActiveGuid(guid);
                if (viewer3D) loadViewableByGuid(viewer3D, guid);
              }}
            />
            {onClose && (
              <button
                css={styles.closeBtn}
                onClick={onClose}
                aria-label="Close viewer">
                ×
              </button>
            )}
          </div>
        </div>
      )}
      {/* flex child that grows; the actual Autodesk canvas lives here */}
      <div css={styles.viewerHost} id="viewer-3d" />
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  viewerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    height: '100%',
    borderLeft: '1px solid #cccccc',
    overflow: 'hidden',
  },
  titleBar: {
    flex: '0 0 40px',
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px',
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    userSelect: 'none',
    zIndex: 1,
  },
  titleText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 'calc(100% - 32px)',
    fontWeight: 500,
  },
  viewerHost: {
    flex: '1 1 auto',
    minHeight: 0,
    minWidth: 0,
    position: 'relative',
  },
  messageContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    flexFlow: 'column',
    position: 'relative',
  },
  errorMessage: {
    color: theme.colors.red,
    fontSize: 24,
    width: '50%',
    textAlign: 'center',
    border: `1px solid ${theme.colors.red}`,
    backgroundColor: theme.colors.white,
    borderRadius: 9,
    padding: '20px 20px',
    boxShadow: '0px 0px 19px rgba(0, 0, 0, 0.07)',
  },
  infoMessage: {
    fontSize: 24,
    width: '50%',
    textAlign: 'center',
    padding: '20px 20px',
  },
  closeBtn: {
    color: '#fff',
    fontSize: 20,
    lineHeight: '20px',
    width: 28,
    height: 28,
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
    ':hover': { opacity: 0.8 },
  },
});
